import type { Me, MembershipStatus, Role } from "@/contexts/AuthContext";
import { PROTECTED_ROUTES, WORKSPACE_UNAVAILABLE_ROUTE } from "@/navigation/routes";

const ROLE_HOME: Record<Role, string> = {
  admin: "/admin",
  instructor: "/instructor",
  student: "/student",
};

/** The landing route for a role, or the sign-in page when unauthenticated. */
export function homeForRole(role: Role | null): string {
  return role ? ROLE_HOME[role] : "/sign-in";
}

/** Resolve a member landing without treating a scalar student role as enrollment. */
export function homeForMember(role: Role | null, workspaces?: Me["workspaces"]): string {
  if (role === "student" && workspaces?.learning.available !== true) {
    return WORKSPACE_UNAVAILABLE_ROUTE;
  }
  return homeForRole(role);
}

const TEACHING_COURSE = /^\/(?:admin\/teaching|instructor)(\/courses\/[^/]+(?:\/[^/]+)?)\/?$/;

/**
 * A teaching course address in the other teaching shell, as this member's own:
 * `/admin/teaching/courses/{c}?view=settings` is `/instructor/courses/{c}?view=settings`
 * for a teacher, and the reverse for a teaching administrator. The course and its
 * view survive (every old address keeps resolving, F5); the server still decides
 * whether this member may read the course. Null when the member has no teaching
 * workspace or the address is already theirs.
 */
export function ownTeachingCourseAddress(
  role: Role | null,
  workspaces: Me["workspaces"] | undefined,
  pathname: string,
  search = "",
  hash = "",
): string | null {
  const match = TEACHING_COURSE.exec(pathname);
  if (!match || workspaces?.teaching?.available !== true) return null;
  const base = role === "admin" ? PROTECTED_ROUTES.adminTeaching.dashboard : role === "instructor" ? PROTECTED_ROUTES.instructor.dashboard : null;
  if (!base || pathname.startsWith(`${base}/`)) return null;
  return `${base}${match[1]}${search}${hash}`;
}

/**
 * A live session whose LMS authority is absent or unknown.
 *
 * Both cases belong on the access-pending page and neither belongs on the
 * sign-in form: the person's credentials are fine, so re-presenting the form
 * offers a remedy for a problem they do not have and loops them back here.
 */
export function needsAccessResolution(status: MembershipStatus): boolean {
  return status === "none" || status === "error";
}
